import type { ProductActionHandler } from "./productCards";
import { productSelectionDetail, type ProductCard } from "./productCards";

/** Presentation only: an explicit, complete one-turn request, never inferred from prose. */
export function requestsGuidedInput(meta: unknown): boolean {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return false;
  const dialogue = (meta as Record<string, unknown>).dialogue;
  if (!dialogue || typeof dialogue !== "object" || Array.isArray(dialogue)) return false;
  const raw = (dialogue as Record<string, unknown>).next_turn_contract;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const c = raw as Record<string, unknown>;
  const keys = ["version", "objective", "objective_disposition", "assistant_act", "expected_responses", "expires_after_turns"];
  if (Object.keys(c).length !== keys.length || Object.keys(c).some(key => !keys.includes(key))) return false;
  if (c.version !== 1 || c.expires_after_turns !== 1 || typeof c.objective_disposition !== "string" || !["general", "notification_request"].includes(c.objective_disposition)) return false;
  if (c.objective !== null && (typeof c.objective !== "string" || c.objective.length > 500)) return false;
  if (typeof c.assistant_act !== "string" || !["ask_for_context", "ask_for_choice", "request_information"].includes(c.assistant_act)) return false;
  const responses = c.expected_responses;
  return Array.isArray(responses) && responses.length > 0 && responses.length <= 4 &&
    responses.every(value => typeof value === "string" && ["accept", "decline", "provide_information", "choose_option", "ask_question", "none"].includes(value)) &&
    responses.some(value => value === "provide_information" || value === "choose_option");
}

export const guidedFieldKeys = ["use", "setting", "appearance", "requirements"] as const;
export type GuidedBrief = Record<typeof guidedFieldKeys[number], string>;
export function normalizeGuidedBrief(raw: unknown): GuidedBrief | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (Object.keys(value).length !== 4 || Object.keys(value).some(key => !guidedFieldKeys.includes(key as typeof guidedFieldKeys[number]))) return null;
  if (!guidedFieldKeys.every(key => typeof value[key] === "string" && value[key].length <= 1500)) return null;
  return Object.fromEntries(guidedFieldKeys.map(key => [key, value[key]])) as GuidedBrief;
}

/** Localized, bounded display copy. Never sent as visitor input or knowledge. */
export function guidedFieldHelp(raw: string | null, locale: "es" | "en"): Partial<Record<typeof guidedFieldKeys[number], { help: string; example: string }>> {
  try {
    if (!raw || raw.length > 8000) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || Object.keys(parsed).some(key => !["es", "en"].includes(key))) return {};
    const entries = parsed[locale];
    if (!entries || typeof entries !== "object" || Array.isArray(entries)) return {};
    const result: ReturnType<typeof guidedFieldHelp> = {};
    for (const key of guidedFieldKeys) {
      const item = entries[key];
      if (!item || typeof item !== "object" || Array.isArray(item) || Object.keys(item).some(key => !["help", "example"].includes(key))) continue;
      if (typeof item.help !== "string" || typeof item.example !== "string" || item.help.length > 240 || item.example.length > 160) continue;
      result[key] = { help: item.help, example: item.example };
    }
    return result;
  } catch { return {}; }
}

/** Inert selection notification; never a cart, message, navigation or authorization. */
export function dispatchWidgetProductSelection(element: Element, product: ProductCard, activationId: string): boolean {
  const detail = productSelectionDetail(product, activationId);
  if (!element.isConnected || !detail) return false;
  element.dispatchEvent(new CustomEvent("aliigo-product-select", { detail: Object.freeze(detail), bubbles: true, composed: true }));
  return true;
}

/** Presentation hints, never knowledge, identity or permission overrides. */
export type WidgetPageContext = {
  url?: string;
  pageType?: string;
  entityFocus?: string;
};
export type WidgetEntryOptions = {
  context?: WidgetPageContext | null;
  locale?: string;
};
export type WidgetAskOptions = WidgetEntryOptions & { questionId: string; activationId?: string };
export type WidgetEntryResult = { status: "opened" | "context_updated" | "submitted" | "duplicate" | "busy" | "unavailable" | "invalid_question" | "invalid_context" | "invalid_locale" };
export type WidgetEntryHandle = {
  open(options?: WidgetEntryOptions): Promise<WidgetEntryResult>;
  ask(options: WidgetAskOptions): Promise<WidgetEntryResult>;
  setContext(context: WidgetPageContext | null): WidgetEntryResult;
  setProductActionHandler?(handler: ProductActionHandler | null): void;
};

export function normalizeWidgetContext(input: unknown, pageUrl: string): WidgetPageContext | null {
  if (input == null) return {};
  if (typeof input !== "object" || Array.isArray(input)) return null;
  const raw = input as Record<string, unknown>;
  if (Object.keys(raw).some((key) => !["url", "pageType", "entityFocus"].includes(key))) return null;
  if (raw.url !== undefined && (typeof raw.url !== "string" || raw.url.length > 2048)) return null;
  const result: WidgetPageContext = {};
  try {
    const page = new URL(pageUrl);
    const url = new URL(typeof raw.url === "string" ? raw.url : pageUrl, page);
    if (!/^https?:$/.test(url.protocol) || url.origin !== page.origin || url.username || url.password) return null;
    url.search = ""; url.hash = "";
    result.url = url.href;
  } catch { return null; }
  if (raw.pageType !== undefined) {
    if (typeof raw.pageType !== "string" || !["home", "service", "treatment", "product", "location", "about", "pricing", "contact", "faq", "policy"].includes(raw.pageType)) return null;
    result.pageType = raw.pageType;
  }
  if (raw.entityFocus !== undefined) {
    if (typeof raw.entityFocus !== "string" || raw.entityFocus.length > 160 || /[<>\p{Cc}\p{Cf}]/u.test(raw.entityFocus)) return null;
    result.entityFocus = raw.entityFocus.trim();
  }
  return result;
}

/** Bounded activation dedup, not question dedup: a new click may ask again. */
export class WidgetActivationGate {
  private claims = new Map<string, number>();
  claim(questionId: string, activationId?: string): boolean {
    if (!activationId) return true;
    const now = Date.now();
    for (const [key, time] of this.claims) if (now - time > 300_000) this.claims.delete(key);
    const key = `${questionId}:${activationId}`;
    if (this.claims.has(key)) return false;
    if (this.claims.size >= 128) this.claims.delete(this.claims.keys().next().value!);
    this.claims.set(key, now);
    return true;
  }
  clear() { this.claims.clear(); }
}

const registryKey = Symbol.for("aliigo.widget.entry.registry");
export type WidgetEntryApi = {
  open(options?: WidgetEntryOptions & { target?: Element | string }): Promise<WidgetEntryResult>;
  ask(options: WidgetAskOptions & { target?: Element | string }): Promise<WidgetEntryResult>;
  setContext(context: WidgetPageContext | null, target?: Element | string): WidgetEntryResult;
  setProductActionHandler(handler: ProductActionHandler | null, target?: Element | string): boolean;
};
declare global { interface Window { AliigoWidget?: WidgetEntryApi } }

export function registerWidgetEntry(element: Element, handle: WidgetEntryHandle): () => void {
  const registry = window as unknown as Record<symbol, WeakMap<Element, WidgetEntryHandle>>;
  const handles = registry[registryKey] ??= new WeakMap<Element, WidgetEntryHandle>();
  handles.set(element, handle);
  const resolve = (target?: Element | string) => {
    try {
      if (target) return handles.get(typeof target === "string" ? document.querySelector(target)! : target);
      const candidates = Array.from(document.querySelectorAll("aliigo-widget, [data-aliigo-entry]")).filter((item) => handles.has(item));
      return candidates.length === 1 ? handles.get(candidates[0]) : undefined;
    } catch { return undefined; }
  };
  window.AliigoWidget ??= {
    open: (options = {}) => resolve(options.target)?.open(options) ?? Promise.resolve({ status: "unavailable" }),
    ask: (options) => resolve(options.target)?.ask(options) ?? Promise.resolve({ status: "unavailable" }),
    setContext: (context, target) => resolve(target)?.setContext(context) ?? { status: "unavailable" },
    setProductActionHandler: (handler, target) => {
      const handle = resolve(target);
      if (!handle?.setProductActionHandler || (handler !== null && typeof handler !== "function")) return false;
      handle.setProductActionHandler(handler); return true;
    },
  };
  return () => { handles.delete(element); };
}
